import { useQueryState, parseAsArrayOf, parseAsInteger, parseAsString, type Parser } from 'nuqs';

/**
 * Хук для удобной работы с query-параметрами массива в URL через библиотеку `nuqs`.
 *
 * Позволяет хранить массив значений в query-строке и управлять им с помощью удобных методов:
 * добавление, удаление, переключение, очистка.
 *
 * @template T Тип элементов массива (по умолчанию `string`)
 *
 * @param {string} queryKey Ключ query-параметра в URL.
 * @param {Parser<T[]>} [parser] Кастомный парсер для значений массива.
 *                               По умолчанию используется строковый парсер (`stringArrayParser`).
 *
 * @returns {{
 *   state: T[] | null,
 *   setState: (value: T[] | null | ((prev: T[] | null) => T[] | null)) => void,
 *   addQueryValue: (id: T) => void,
 *   removeQueryValue: (id: T) => void,
 *   toggleQueryValue: (id: T) => void,
 *   clearQueryValue: () => void,
 *   valueInQueryState: (value: T) => boolean
 * }}
 *
 * @example
 * const { state, addQueryValue, removeQueryValue, toggleQueryValue } =
 *   useNuqsArrayQueryState('tags');
 *
 * addQueryValue('react');    // Добавит "react" в query-параметр `tags`
 * removeQueryValue('vue');   // Удалит "vue" из query-параметра `tags`
 * toggleQueryValue('svelte'); // Переключит наличие "svelte" в массиве
 *
 */

export const numberArrayParser = parseAsArrayOf(parseAsInteger, ',').withDefault([]);
export const stringArrayParser = parseAsArrayOf(parseAsString,  ',').withDefault([]);

export const useNuqsArrayQueryState = <T = string>(
    queryKey: string,
    parser?: Parser<T[]>,
) => {
    const effectiveParser = (parser ?? (stringArrayParser as unknown as Parser<T[]>));
    const [state, setState] = useQueryState<T[]>(queryKey, effectiveParser);

    const addQueryValue = (id: T) =>
        setState(prev => {
            const next = new Set(prev ?? []);
            next.add(id);
            return Array.from(next);
        });

    const removeQueryValue = (id: T) =>
        setState(prev => (prev ?? []).filter(x => x !== id));

    const toggleQueryValue = (id: T) =>
        setState(prev => {
            const s = new Set(prev ?? []);
            if (s.has(id)) {
                s.delete(id);
            } else {
                s.add(id);
            }
            return Array.from(s);
        });

    const clearQueryValue = () => setState(null);
    const valueInQueryState = (value: T) => (state ?? []).includes(value);

    return { state, setState, addQueryValue, removeQueryValue, toggleQueryValue, clearQueryValue, valueInQueryState };
};
